import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, PlayCircle } from "lucide-react";
import { BOOK_URL, BOOK_REVIEW_VIDEO_ID } from "@/lib/site";
import { VideoModal } from "@/components/site/VideoModal";
import cover from "@/assets/book-cover.jpg";
import r1 from "@/assets/reader1.jpg";
import r2 from "@/assets/reader2.jpg";
import r3 from "@/assets/reader3.jpg";
import r4 from "@/assets/reader4.jpg";
import r5 from "@/assets/client-holding-book.jpeg";

const ALT = "Reader holding a copy of From Regular to Rich by Shawn King.";

// anchor first, then four supporting images
const readers = [
  { src: r2, alt: ALT },
  { src: r5, alt: ALT },
  { src: r1, alt: ALT },
  { src: r3, alt: "Reader holding two copies of From Regular to Rich by Shawn King." },
  { src: r4, alt: ALT },
];

export const BookFeature = () => {
  const [reviewOpen, setReviewOpen] = useState(false);
  return (
  <section id="book" data-testid="book-section" className="bg-[#FBF8F1] py-24 md:py-32">
    <div className="container-lr grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      {/* Left: cover + copy */}
      <div>
        <div className="flex items-center gap-4">
          <span className="gold-rule" />
          <p className="eyebrow">From Regular to Rich</p>
        </div>
        <h2 className="mt-6 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl lg:text-5xl leading-[1.08]">
          A Financial Framework Built for <span className="accent-word">Everyday</span> People.
        </h2>

        <div className="mt-8 flex flex-col sm:flex-row gap-8 items-start">
          <div className="relative shrink-0">
            <div className="absolute -top-3 -left-3 h-14 w-14 border-t border-l border-gold/70" />
            <img
              src={cover}
              alt="From Regular to Rich by Shawn King — book cover"
              loading="lazy"
              data-testid="book-cover-image"
              className="relative w-52 sm:w-56 rounded-xl shadow-[0_40px_70px_-30px_rgba(17,17,17,0.6)]"
            />
          </div>
          <div className="space-y-4 text-slate text-base leading-relaxed">
            <p>
              <em>From Regular to Rich</em> explains how ordinary people can improve
              cash flow, correct financial mistakes, build better systems, and create a
              life worth living.
            </p>
            <p>
              The book combines Shawn’s personal experience with practical education
              around family banking, debt payoff, passive income, financial habits, and
              long-term legacy planning.
            </p>
          </div>
        </div>

        <p className="mt-8 eyebrow text-bronze" data-testid="reader-proof-label">
          Real Readers. Real Financial Conversations.
        </p>
        <p className="mt-3 text-slate text-[0.95rem] leading-relaxed max-w-xl">
          From Regular to Rich is helping everyday people begin new conversations about
          cash flow, financial control, family banking, and building a life worth living.
        </p>

        <a
          href={BOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="book-cta"
          className="group mt-8 inline-flex items-center gap-2 btn-base btn-primary"
        >
          Learn More About the Book
          <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
        <button
          type="button"
          onClick={() => setReviewOpen(true)}
          data-testid="watch-reader-reviews"
          className="mt-3 sm:mt-8 sm:ml-3 inline-flex items-center gap-2 btn-base btn-outline-dark"
        >
          <PlayCircle size={18} />
          Watch Reader Reviews
        </button>
      </div>

      {/* Right: reader collage (desktop) */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="hidden lg:grid grid-cols-3 grid-rows-3 gap-3 h-[560px]"
        data-testid="reader-gallery-desktop"
      >
        <figure className="col-span-2 row-span-2 overflow-hidden rounded-card border border-taupe/40 shadow-[0_24px_50px_-30px_rgba(17,17,17,0.4)]">
          <img src={readers[0].src} alt={readers[0].alt} loading="lazy" className="h-full w-full object-cover" />
        </figure>
        <figure className="overflow-hidden rounded-card border border-taupe/40">
          <img src={readers[1].src} alt={readers[1].alt} loading="lazy" className="h-full w-full object-cover" />
        </figure>
        <figure className="overflow-hidden rounded-card border border-taupe/40">
          <img src={readers[2].src} alt={readers[2].alt} loading="lazy" className="h-full w-full object-cover" />
        </figure>
        <figure className="overflow-hidden rounded-card border border-taupe/40">
          <img src={readers[3].src} alt={readers[3].alt} loading="lazy" className="h-full w-full object-cover" />
        </figure>
        <figure className="col-span-2 overflow-hidden rounded-card border border-taupe/40">
          <img src={readers[4].src} alt={readers[4].alt} loading="lazy" className="h-full w-full object-cover" />
        </figure>
      </motion.div>
    </div>

    {/* Reader gallery (mobile / tablet) — scroll-snap, keyboard accessible */}
    <div
      className="lg:hidden container-lr mt-12 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-0 [scrollbar-width:thin]"
      role="group"
      aria-label="Readers holding From Regular to Rich"
      tabIndex={0}
      data-testid="reader-gallery-mobile"
    >
      {readers.map((r, i) => (
        <figure
          key={i}
          className="snap-center shrink-0 w-[68%] sm:w-[44%] overflow-hidden rounded-card border border-taupe/40"
        >
          <img src={r.src} alt={r.alt} loading="lazy" className="w-full h-72 object-cover" />
        </figure>
      ))}
    </div>

    <VideoModal
      open={reviewOpen}
      onClose={() => setReviewOpen(false)}
      videoId={BOOK_REVIEW_VIDEO_ID}
      title="What Readers Are Saying About From Regular to Rich"
      description="Hear directly from readers about the ideas, conversations, and financial questions the book helped them explore. Individual experiences vary, and no specific financial outcome is promised."
    />
  </section>
  );
};
